import { useState, useEffect, useCallback } from 'react';
import { useAppStore } from '@/store/appStore';
import {
  fetchWorkspaceUsers,
  createWorkspaceUser,
  updateWorkspaceUser,
  deleteWorkspaceUser,
} from '@/services/api';
import { toast } from '@/components/common/toast';
import type { WorkspaceUser, UserPermissions } from '@/types';

const PERM_KEYS: (keyof UserPermissions)[] = ['njHighlight', 'abnormalSales', 'adminBackend', 'adminPanel'];
const PERM_LABELS: Record<keyof UserPermissions, string> = {
  njHighlight: 'AI Highlight',
  abnormalSales: 'Abnormal Sales',
  adminBackend: 'Admin Backend',
  adminPanel: 'Permission Mgmt',
};

const cardStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid #ececec', borderRadius: 2,
  padding: '16px 20px', marginBottom: 16,
};

const thStyle: React.CSSProperties = {
  padding: '10px 12px', background: '#fafafa', borderBottom: '1px solid #ececec',
  color: '#6b7280', fontSize: 12, fontWeight: 600, textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px', borderBottom: '1px solid #f1f5f9',
  color: '#4b5563', fontSize: 13,
};

export default function AdminAuthApp() {
  const hasPermission = useAppStore((s) => s.hasPermission);
  const authorized = hasPermission('adminPanel');

  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({
    email: '',
    displayName: '',
    permissions: { njHighlight: false, abnormalSales: false, adminBackend: false, adminPanel: false },
  });

  const loadUsers = useCallback(() => {
    setLoading(true);
    fetchWorkspaceUsers()
      .then(setUsers)
      .catch(() => { /* toast handled by interceptor */ })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (authorized) loadUsers(); }, [authorized, loadUsers]);

  const resetForm = () => {
    setForm({
      email: '',
      displayName: '',
      permissions: { njHighlight: false, abnormalSales: false, adminBackend: false, adminPanel: false },
    });
    setEditId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    try {
      if (editId) {
        await updateWorkspaceUser(editId, form);
        toast.success('权限已更新');
      } else {
        await createWorkspaceUser(form);
        toast.success('用户已添加');
      }
      resetForm();
      loadUsers();
    } catch (err: any) {
      // toast handled by interceptor
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this email permission mapping?')) return;
    try {
      await deleteWorkspaceUser(id);
      toast.success('用户已删除');
      loadUsers();
    } catch (err: any) {
      // toast handled by interceptor
    }
  };

  const togglePerm = async (user: WorkspaceUser, permKey: keyof UserPermissions) => {
    const updated = { ...user.permissions, [permKey]: !user.permissions[permKey] };
    try {
      await updateWorkspaceUser(user.id, { permissions: updated });
      loadUsers();
    } catch (err: any) {
      // toast handled by interceptor
    }
  };

  if (!authorized) {
    return (
      <div style={{ height: '100%', background: '#f3f4f6', overflow: 'auto', padding: '24px 20px', fontFamily: 'inherit' }}>
        <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: '20px 22px', color: '#64748b', fontSize: 14 }}>
          Access denied. Your account does not have Permission Management access.
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', background: '#f3f4f6', overflow: 'auto', padding: '18px 20px 22px', fontFamily: 'inherit' }}>
      <div style={{ color: '#eb1923', fontSize: 28, lineHeight: 1.1, marginBottom: 4 }}>Permission Management</div>
      <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 20 }}>
        Manage D365 AccessToken email-based access control for all four pages.
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>D365 Email Permissions</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              Only D365 email login is enabled. Map each email to the four page permissions below.
            </div>
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid #eb1923', background: '#eb1923', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            + Add Email
          </button>
        </div>

        {showForm && (
          <div style={{ background: '#f9fafb', border: '1px solid #e2e8f0', borderRadius: 4, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
              {editId ? 'Edit Email Permission' : 'New Email Permission'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Email</div>
                <input style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#f9fafb', width: '100%' }}
                  value={form.email} placeholder="name@example.com"
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Display Name (optional)</div>
                <input style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#f9fafb', width: '100%' }}
                  value={form.displayName} placeholder="Shown after login"
                  onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Page Access</div>
              {PERM_KEYS.map((key) => (
                <label key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4b5563', cursor: 'pointer', marginRight: 16 }}>
                  <input type="checkbox" checked={form.permissions[key]}
                    onChange={(e) => setForm((p) => ({ ...p, permissions: { ...p.permissions, [key]: e.target.checked } }))} />
                  {PERM_LABELS[key]}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSave} style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid #eb1923', background: '#eb1923', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
              <button onClick={resetForm} style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid #e2e8f0', background: 'white', color: '#4b5563', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ color: '#94a3b8', fontSize: 13, padding: 12 }}>Loading...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Display Name</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>AI Highlight</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Abnormal Sales</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Admin Backend</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Permission Mgmt</th>
                <th style={{ ...thStyle, width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={tdStyle}>{u.email}</td>
                  <td style={tdStyle}>{u.displayName || '-'}</td>
                  {PERM_KEYS.map((key) => (
                    <td key={key} style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={!!u.permissions[key]}
                        onChange={() => togglePerm(u, key)}
                        style={{ cursor: 'pointer', width: 16, height: 16 }} />
                    </td>
                  ))}
                  <td style={tdStyle}>
                    <button onClick={() => { setForm({ email: u.email, displayName: u.displayName || '', permissions: { ...u.permissions } }); setEditId(u.id); setShowForm(true); }}
                      style={{ marginRight: 6, padding: '6px 14px', borderRadius: 4, border: '1px solid #e2e8f0', background: 'white', color: '#4b5563', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Edit
                    </button>
                    <button onClick={() => handleDelete(u.id)}
                      style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid #fecaca', background: 'white', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
